// src/phase4/ui/TowerUpgradePanel.ts
// Tower Upgrade Radial Menu - Pure Phaser UI component
// Displays tower info and radial menu with upgrade/sell options

import Phaser from 'phaser';
import { TowerType } from '../types/tower';

// Upgrade info for display
export interface UpgradeInfo {
  canUpgrade: boolean;
  cost: number;
  nextLevel: number;
  dpsIncrease?: number;
  rangeIncrease?: number;
  newEffects?: string[];
}

// Tower info for display
export interface TowerPanelInfo {
  id: string;
  type: TowerType;
  name: string;
  level: number;
  dps: number;
  range: number;
  fireRate: number;
  specialEffects?: string[];
  pathName: string;
}

// Menu action option
interface MenuOption {
  key: 'upgrade' | 'sell' | 'cancel';
  label: string;
  icon: string;
  color: number;
  description?: string;
}

const MENU_OPTIONS: MenuOption[] = [
  {
    key: 'upgrade',
    label: 'Upgrade',
    icon: '↑',
    color: 0x5cd6a8,
    description: '+Level',
  },
  {
    key: 'sell',
    label: 'Sell',
    icon: '$',
    color: 0xe05545,
    description: 'Refund 50%',
  },
  {
    key: 'cancel',
    label: 'Close',
    icon: '×',
    color: 0x6b6459,
  },
];

export class TowerUpgradePanel extends Phaser.GameObjects.Container {
  private towerId: string = '';
  private menuRadius = 100;
  private optionNodes: RadialMenuNode[] = [];
  private centerCircle?: Phaser.GameObjects.Graphics;
  private outerRing?: Phaser.GameObjects.Graphics;
  private towerIcon?: Phaser.GameObjects.Text;
  private levelText?: Phaser.GameObjects.Text;
  private isExpanded = false;

  private onUpgrade?: (towerId: string) => void;
  private onSell?: (towerId: string) => void;
  private onCancel?: () => void;

  // Colors for different tower types
  private readonly TYPE_COLORS: Record<TowerType, number> = {
    [TowerType.CPU_MAIN]: 0x5890e0,
    [TowerType.LANGUAGE_SIDE]: 0x52c07a,
    [TowerType.NETWORK_SIDE]: 0xe6b832,
  };

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
    this.createMenu();
    this.setVisible(false);
    this.setDepth(1000);
  }

  private createMenu(): void {
    // Outer ring
    this.outerRing = this.scene.add.graphics();
    this.add(this.outerRing);

    // Center circle (tower info)
    this.centerCircle = this.scene.add.graphics();
    this.add(this.centerCircle);

    // Tower icon in center
    this.towerIcon = this.scene.add.text(0, -8, 'T', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.add(this.towerIcon);

    // Level badge
    this.levelText = this.scene.add.text(0, 14, 'Lv.1', {
      fontSize: '12px',
      color: '#9b9488',
      fontFamily: 'monospace',
    }).setOrigin(0.5);
    this.add(this.levelText);

    // Create radial nodes for menu options
    const angleStep = (Math.PI * 2) / MENU_OPTIONS.length;
    MENU_OPTIONS.forEach((option, index) => {
      const angle = angleStep * index - Math.PI / 2; // Start from top
      const node = new RadialMenuNode(
        this.scene,
        option,
        angle,
        this.menuRadius,
        (key) => this.handleMenuSelect(key)
      );
      this.add(node);
      this.optionNodes.push(node);
    });

    // Draw initial state
    this.drawCenterCircle(0x2a2824, 0.8);
    this.drawOuterRing(0xe6a640, 0.4);
  }

  private drawCenterCircle(color: number, alpha: number): void {
    if (!this.centerCircle) return;
    this.centerCircle.clear();
    this.centerCircle.fillStyle(color, alpha);
    this.centerCircle.fillCircle(0, 0, 40);
    this.centerCircle.lineStyle(2, 0xe6a640, 0.6);
    this.centerCircle.strokeCircle(0, 0, 40);
  }

  private drawOuterRing(color: number, alpha: number): void {
    if (!this.outerRing) return;
    this.outerRing.clear();
    this.outerRing.lineStyle(2, color, alpha);
    this.outerRing.strokeCircle(0, 0, this.menuRadius);

    // Dashed effect
    const dashLength = 10;
    const gapLength = 6;
    const circumference = Math.PI * 2 * this.menuRadius;
    const totalDashes = Math.floor(circumference / (dashLength + gapLength));

    for (let i = 0; i < totalDashes; i++) {
      const startAngle = (i / totalDashes) * Math.PI * 2;
      const endAngle = startAngle + (dashLength / this.menuRadius);
      this.outerRing.arc(0, 0, this.menuRadius, startAngle, endAngle, false);
    }
  }

  private handleMenuSelect(key: string): void {
    switch (key) {
      case 'upgrade':
        this.onUpgrade?.(this.towerId);
        break;
      case 'sell':
        this.onSell?.(this.towerId);
        break;
      case 'cancel':
        this.hide();
        this.onCancel?.();
        break;
    }
  }

  show(towerId: string, x: number, y: number): void {
    this.towerId = towerId;
    this.setPosition(x, y);
    this.setVisible(true);
    this.isExpanded = false;

    // Reset all nodes
    this.optionNodes.forEach(node => node.reset());

    // Draw compact center
    this.drawCenterCircle(0x2a2824, 0.8);
    this.drawOuterRing(0xe6a640, 0.3);

    // Scale up animation
    this.setScale(0);
    this.scene.tweens.add({
      targets: this,
      scaleX: { from: 0, to: 1 },
      scaleY: { from: 0, to: 1 },
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => this.expandMenu(),
    });
  }

  private expandMenu(): void {
    if (this.isExpanded) return;
    this.isExpanded = true;

    // Draw full outer ring
    this.drawOuterRing(0xe6a640, 0.5);

    // Expand each node with stagger
    this.optionNodes.forEach((node, index) => {
      this.scene.time.delayedCall(index * 60, () => node.expand());
    });
  }

  hide(): void {
    // Contract all nodes
    this.optionNodes.forEach(node => node.contract());

    this.scene.time.delayedCall(200, () => {
      this.scene.tweens.add({
        targets: this,
        scaleX: { to: 0 },
        scaleY: { to: 0 },
        duration: 150,
        ease: 'Back.easeIn',
        onComplete: () => {
          this.setVisible(false);
        },
      });
    });
  }

  updateInfo(info: TowerPanelInfo, upgrade: UpgradeInfo): void {
    const typeColor = this.TYPE_COLORS[info.type];

    // Update center circle color based on tower type
    this.drawCenterCircle(typeColor, 0.6);

    // Update tower icon (first letter of name)
    this.towerIcon?.setText(info.name[0] || 'T');
    this.levelText?.setText(`Lv.${info.level}`);

    // Update outer ring color
    this.drawOuterRing(typeColor, 0.5);

    // Update upgrade/sell nodes with info
    const upgradeNode = this.optionNodes.find(n => n.getKey() === 'upgrade');
    const sellNode = this.optionNodes.find(n => n.getKey() === 'sell');

    if (upgradeNode) {
      upgradeNode.setDescription(
        upgrade.canUpgrade ? `+${upgrade.cost}₣` : 'MAX'
      );
      upgradeNode.setEnabled(upgrade.canUpgrade);
    }

    if (sellNode) {
      // Sell value would be calculated based on tower level and cost
      const sellValue = Math.floor(upgrade.cost * info.level * 0.5);
      sellNode.setDescription(`${sellValue}₣`);
    }
  }

  setOnUpgrade(cb: (towerId: string) => void): void {
    this.onUpgrade = cb;
  }

  setOnSell(cb: (towerId: string) => void): void {
    this.onSell = cb;
  }

  setOnCancel(cb: () => void): void {
    this.onCancel = cb;
  }

  destroy(): void {
    this.optionNodes.forEach(node => node.destroy());
    super.destroy();
  }
}

// Individual radial menu node
class RadialMenuNode extends Phaser.GameObjects.Container {
  private option: MenuOption;
  private nodeAngle: number;
  private radius: number;
  private background: Phaser.GameObjects.Graphics;
  private iconText?: Phaser.GameObjects.Text;
  private labelText?: Phaser.GameObjects.Text;
  private descText?: Phaser.GameObjects.Text;
  private expanded = false;
  private enabled = true;
  private onSelect?: (key: string) => void;

  constructor(
    scene: Phaser.Scene,
    option: MenuOption,
    angle: number,
    radius: number,
    onSelect?: (key: string) => void
  ) {
    super(scene, 0, 0);
    this.option = option;
    this.nodeAngle = angle;
    this.radius = radius;
    this.onSelect = onSelect;

    // Start at center
    this.setPosition(0, 0);
    this.setAlpha(0);

    // Create background
    this.background = scene.add.graphics();
    this.add(this.background);

    // Create content
    this.createContent();

    // Setup interaction
    this.setupInteraction();
  }

  private createContent(): void {
    const size = 40;

    // Background circle
    this.background.fillStyle(this.option.color, 0.85);
    this.background.fillCircle(0, 0, size);
    this.background.lineStyle(2, 0xffffff, 0.2);
    this.background.strokeCircle(0, 0, size);

    // Icon
    this.iconText = this.scene.add.text(0, -6, this.option.icon, {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.add(this.iconText);

    // Label below
    this.labelText = this.scene.add.text(0, size + 6, this.option.label, {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.labelText.setAlpha(0);
    this.add(this.labelText);

    // Description (cost, value, etc.)
    if (this.option.description) {
      this.descText = this.scene.add.text(0, size + 20, this.option.description, {
        fontSize: '10px',
        color: '#ffd700',
        fontFamily: 'monospace',
      }).setOrigin(0.5);
      this.descText.setAlpha(0);
      this.add(this.descText);
    }
  }

  private setupInteraction(): void {
    const hitArea = this.scene.add.circle(0, 0, 45, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });

    hitArea.on('pointerover', () => {
      if (!this.expanded || !this.enabled) return;
      this.scene.tweens.add({
        targets: this,
        scaleX: 1.15,
        scaleY: 1.15,
        duration: 100,
        ease: 'Quad.easeOut',
      });
    });

    hitArea.on('pointerout', () => {
      if (!this.expanded) return;
      this.scene.tweens.add({
        targets: this,
        scaleX: 1,
        scaleY: 1,
        duration: 100,
        ease: 'Quad.easeOut',
      });
    });

    hitArea.on('pointerdown', () => {
      if (!this.expanded || !this.enabled) return;
      this.scene.sound.play('click');

      // Pulse effect
      this.scene.tweens.add({
        targets: this,
        scaleX: 0.85,
        scaleY: 0.85,
        duration: 50,
        yoyo: true,
        ease: 'Quad.easeInOut',
        onComplete: () => {
          this.onSelect?.(this.option.key);
        },
      });
    });

    this.add(hitArea);
  }

  expand(): void {
    this.expanded = true;
    const targetX = Math.cos(this.nodeAngle) * this.radius;
    const targetY = Math.sin(this.nodeAngle) * this.radius;

    this.scene.tweens.add({
      targets: this,
      x: { from: 0, to: targetX },
      y: { from: 0, to: targetY },
      alpha: { from: 0, to: 1 },
      duration: 300,
      ease: 'Back.easeOut',
    });

    // Show labels with delay
    this.scene.time.delayedCall(100, () => {
      const targets: Phaser.GameObjects.GameObject[] = [this.labelText!];
      if (this.descText) targets.push(this.descText);
      this.scene.tweens.add({
        targets,
        alpha: 1,
        duration: 150,
      });
    });
  }

  contract(): void {
    this.scene.tweens.add({
      targets: this,
      x: 0,
      y: 0,
      alpha: 0,
      duration: 200,
      ease: 'Back.easeIn',
    });
  }

  reset(): void {
    this.expanded = false;
    this.setPosition(0, 0);
    this.setAlpha(0);
    this.setScale(1);
    this.labelText?.setAlpha(0);
    this.descText?.setAlpha(0);
  }

  setDescription(text: string): void {
    this.descText?.setText(text);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.scene.tweens.add({
        targets: [this.background, this.iconText, this.labelText, this.descText],
        alpha: 0.4,
        duration: 150,
      });
    }
  }

  getKey(): string {
    return this.option.key;
  }
}

export default TowerUpgradePanel;
